import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import type {
  CompanyPositionEnrichProps,
  PositionEnrichmentQuestion,
  PositionEnrichmentTurn,
} from '@/types/company'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

interface AdvanceResponse {
  status: 'in_progress' | 'complete' | 'skipped'
  question?: PositionEnrichmentQuestion
  complete?: boolean
  errors?: string[]
}

export default function CompanyPositionEnrich({ position, enrichment }: CompanyPositionEnrichProps) {
  const [turns, setTurns] = useState<PositionEnrichmentTurn[]>(enrichment.turns)
  const [current, setCurrent] = useState<PositionEnrichmentQuestion | null>(enrichment.currentQuestion)
  const [answer, setAnswer] = useState('')
  const [done, setDone] = useState(enrichment.status !== 'in_progress' && enrichment.currentQuestion === null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submitAnswer() {
    if (current === null || answer.trim() === '' || submitting) {
      return
    }
    setSubmitting(true)
    setError(null)

    const answered = current
    try {
      const response = await fetch(`/api/company/positions/${position.id}/enrichment/answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ question_id: answered.id, answer }),
      })
      const data = (await response.json()) as AdvanceResponse

      if (!response.ok) {
        setError(data.errors?.join(' ') ?? "We couldn't save your answer — try again.")
        return
      }

      setTurns((prior) => [...prior, { id: answered.id, question: answered.question, answer }])
      setAnswer('')

      if (data.complete === true || data.question === undefined) {
        setCurrent(null)
        setDone(true)
        return
      }
      setCurrent(data.question)
    } catch {
      setError("We couldn't reach the server — try again or skip.")
    } finally {
      setSubmitting(false)
    }
  }

  function skip() {
    router.post(`/company/positions/${position.id}/enrich/skip`)
  }

  function returnToPosition() {
    router.get(`/company/positions/${position.id}`)
  }

  return (
    <>
      <Head title={`Enrich ${position.title} — Hyrin`} />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />

        <main className="mx-auto max-w-[720px] px-6 py-[52px]">
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            Enrich position
          </p>
          <h1 className="mb-[14px] font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
            {position.title}
          </h1>

          <p className="mb-6 rounded-[8px] bg-background px-4 py-3 font-sans text-[13px] leading-[1.55] text-muted-foreground">
            Best answered by an engineer on your team. A few concrete details sharpen the match —
            you can skip anytime.
          </p>

          <div className="rounded-card bg-background p-8 shadow-sm">
            {turns.length > 0 && (
              <ul className="mb-6 flex flex-col gap-3 border-b border-border pb-6">
                {turns.map((turn) => (
                  <li key={turn.id} className="flex flex-col gap-[2px]">
                    <span className="font-mono text-[12px] text-primary">✓ {turn.question}</span>
                    <span className="font-sans text-[14px] text-foreground">{turn.answer}</span>
                  </li>
                ))}
              </ul>
            )}

            {done && (
              <div className="flex flex-col gap-4">
                <p className="font-sans text-[15px] leading-[1.55] text-foreground">
                  That's everything — thanks. These answers now help match the right engineers.
                </p>
                <div>
                  <Button onClick={returnToPosition}>Back to position</Button>
                </div>
              </div>
            )}

            {!done && current !== null && (
              <div className="flex flex-col gap-4">
                <p className="font-heading text-[18px] font-bold leading-[1.2] tracking-[-0.01em] text-foreground">
                  {current.question}
                </p>

                {current.suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-[8px]">
                    {current.suggestions.map((suggestion) => (
                      <button
                        key={`${current.id}-${suggestion}`}
                        type="button"
                        onClick={() => setAnswer(suggestion)}
                        className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-foreground transition-colors hover:bg-muted"
                      >
                        <span className="size-[5px] rounded-full bg-primary" />
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                <Input
                  type="text"
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                  placeholder="Type an answer…"
                  disabled={submitting}
                />

                {error !== null && (
                  <p className="font-sans text-[12px] text-destructive">{error}</p>
                )}

                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" onClick={skip} disabled={submitting}>
                    Skip
                  </Button>
                  <Button onClick={submitAnswer} disabled={submitting || answer.trim() === ''}>
                    {submitting ? 'Loading…' : 'Answer →'}
                  </Button>
                </div>
              </div>
            )}

            {!done && current === null && (
              <div className="flex flex-col gap-4">
                <p className="font-sans text-[15px] leading-[1.55] text-muted-foreground">
                  We couldn't load a question right now. You can try again or skip.
                </p>
                <div className="flex items-center gap-3">
                  <Button variant="ghost" onClick={skip}>
                    Skip
                  </Button>
                  <Button onClick={returnToPosition}>Back to position</Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  )
}
